import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  IconButton,
  CircularProgress,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions
} from '@mui/material';
import {
  ArrowBack,
  Add,
  Edit,
  Delete,
  Person,
  Phone,
  Email,
  LockReset,
  AdminPanelSettings
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import principalAPI from '../../services/principalAPI';
import { useAuth } from '../../context/AuthContext';

const SchoolAdmins = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, admin: null });
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [resetDialog, setResetDialog] = useState({ open: false, admin: null });
  const [resetLoading, setResetLoading] = useState(false);

  useEffect(() => {
    let isMounted = true;

    const fetchData = async () => {
      try {
        const response = await principalAPI.getSchoolAdmins();
        if (isMounted) {
          setAdmins(response.data.schoolAdmins || []);
        }
      } catch (error) {
        console.error('Error fetching school admins:', error);
        if (isMounted) {
          setAdmins([]);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchData();

    return () => {
      isMounted = false;
    };
  }, []);

  const handleDeleteClick = (e, admin) => {
    e.stopPropagation();
    setDeleteDialog({ open: true, admin });
  };

  const handleDeleteConfirm = async () => {
    if (!deleteDialog.admin) return;
    setDeleteLoading(true);
    try {
      await principalAPI.deleteSchoolAdmin(deleteDialog.admin.id);
      setAdmins(admins.filter(a => a.id !== deleteDialog.admin.id));
      setDeleteDialog({ open: false, admin: null });
    } catch (error) {
      console.error('Error deleting school admin:', error);
      alert(error.response?.data?.message || 'Failed to delete school admin');
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleResetPassword = async () => {
    setResetLoading(true);
    try {
      await principalAPI.resetSchoolAdminPassword(resetDialog.admin.id);
      alert('Password reset link sent to the admin\'s email!');
      setResetDialog({ open: false, admin: null });
    } catch (error) {
      console.error('Error resetting password:', error);
      alert(error.response?.data?.message || 'Failed to send reset link');
    } finally {
      setResetLoading(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ mt: { xs: 2, md: 4 }, mb: 4, px: { xs: 1.5, sm: 3 } }}>
      {/* Header */}
      <Box display="flex" flexDirection={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', sm: 'center' }} gap={2} mb={3}>
        <Typography variant="h4" fontWeight="bold" sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>
          Manage School Admins
        </Typography>
        <Box display="flex" gap={1} flexWrap="wrap">
          <Button
            variant="outlined"
            startIcon={<ArrowBack />}
            onClick={() => navigate('/principal/dashboard')}
          >
            Back to Dashboard
          </Button>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => navigate('/principal/school-admins/new')}
          >
            Add School Admin
          </Button>
        </Box>
      </Box>

      {/* Admins List */}
      {admins.length === 0 ? (
        <Paper sx={{ p: 6, textAlign: 'center' }}>
          <AdminPanelSettings sx={{ fontSize: 60, color: 'text.disabled', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No school admins found
          </Typography>
          <Typography variant="body2" color="text.secondary" mb={2}>
            Click "Add School Admin" to create one. School admins can help manage teachers, students, classes, and finances.
          </Typography>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => navigate('/principal/school-admins/new')}
          >
            Add School Admin
          </Button>
        </Paper>
      ) : (
        <Box display="flex" flexDirection="column" gap={1.5}>
          {admins.map((admin) => (
            <Paper
              key={admin.id}
              elevation={1}
              sx={{
                overflow: 'hidden',
                transition: 'all 0.3s ease',
                '&:hover': { boxShadow: 3 },
              }}
            >
              <Box display="flex" alignItems="center" px={2.5} py={1.5} gap={2}>
                {/* Avatar */}
                <Box
                  sx={{
                    width: 42,
                    height: 42,
                    borderRadius: '50%',
                    background: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  <Typography variant="body1" fontWeight="bold" color="white">
                    {(admin.name || 'A').charAt(0).toUpperCase()}
                  </Typography>
                </Box>

                {/* Name */}
                <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                  <Typography variant="body1" fontWeight={600} noWrap>
                    {admin.name || 'N/A'}
                  </Typography>
                  <Box display="flex" alignItems="center" gap={0.5}>
                    <Email sx={{ fontSize: 14, color: 'text.secondary' }} />
                    <Typography variant="caption" color="text.secondary">
                      {admin.email}
                    </Typography>
                  </Box>
                </Box>

                {/* Phone */}
                <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: 0.5 }}>
                  <Phone sx={{ fontSize: 16, color: 'text.secondary' }} />
                  <Typography variant="body2" color="text.secondary">
                    {admin.phone || '—'}
                  </Typography>
                </Box>

                {/* Action Buttons */}
                <Box display="flex" gap={0.5} sx={{ flexShrink: 0 }}>
                  <Tooltip title="Reset Password">
                    <IconButton
                      size="small"
                      color="warning"
                      onClick={(e) => {
                        e.stopPropagation();
                        setResetDialog({ open: true, admin });
                      }}
                      sx={{
                        bgcolor: 'warning.50',
                        '&:hover': { bgcolor: 'warning.100' },
                      }}
                    >
                      <LockReset fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Edit School Admin">
                    <IconButton
                      size="small"
                      color="primary"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/principal/school-admins/edit/${admin.id}`);
                      }}
                      sx={{
                        bgcolor: 'primary.50',
                        '&:hover': { bgcolor: 'primary.100' },
                      }}
                    >
                      <Edit fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete School Admin">
                    <IconButton
                      size="small"
                      color="error"
                      onClick={(e) => handleDeleteClick(e, admin)}
                      sx={{
                        bgcolor: 'error.50',
                        '&:hover': { bgcolor: 'error.100' },
                      }}
                    >
                      <Delete fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>
            </Paper>
          ))}
        </Box>
      )}

      {/* Results Summary */}
      {admins.length > 0 && (
        <Box mt={2}>
          <Typography variant="body2" color="text.secondary">
            Showing {admins.length} school admin{admins.length !== 1 ? 's' : ''}
          </Typography>
        </Box>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialog.open}
        onClose={() => setDeleteDialog({ open: false, admin: null })}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ color: 'error.main', fontWeight: 600 }}>
          Delete School Admin
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete <strong>{deleteDialog.admin?.name}</strong>? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeleteDialog({ open: false, admin: null })} disabled={deleteLoading}>
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            disabled={deleteLoading}
            startIcon={deleteLoading ? <CircularProgress size={16} /> : <Delete />}
          >
            {deleteLoading ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reset Password Dialog */}
      <Dialog
        open={resetDialog.open}
        onClose={() => setResetDialog({ open: false, admin: null })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Reset Password</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2, mt: 1 }}>
            A password reset link will be emailed to <strong>{resetDialog.admin?.name}</strong> ({resetDialog.admin?.email}). The link expires in 1 hour.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResetDialog({ open: false, admin: null })}>
            Cancel
          </Button>
          <Button
            onClick={handleResetPassword}
            variant="contained"
            color="warning"
            disabled={resetLoading}
            startIcon={resetLoading ? <CircularProgress size={20} /> : <LockReset />}
          >
            {resetLoading ? 'Sending...' : 'Send Reset Link'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default SchoolAdmins;
